import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon } from 'lucide-react';
import { Panel } from '../../components/ui/Panel';
import { TextInput } from '../../components/ui/TextInput';
import { SelectInput } from '../../components/ui/SelectInput';
import { AccountStateBadge } from '../../components/admin/AccountStateBadge';
import { EmptyState } from '../../components/EmptyState';
import { customerAccounts } from '../../data/adminCustomers';
import { getPlan } from '../../data/plans';
import type { AccountState } from '../../types/admin';
import { relativeTime } from '../../utils/format';
type StateFilter = AccountState | 'all';
type PlanFilter = 'all' | 'starter' | 'standard' | 'pro';
export function AdminCustomers() {
  const [query, setQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<StateFilter>('all');
  const [planFilter, setPlanFilter] = useState<PlanFilter>('all');
  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return customerAccounts.filter(customer => {
      if (stateFilter !== 'all' && customer.state !== stateFilter) {
        return false;
      }
      if (planFilter !== 'all' && customer.planId !== planFilter) {
        return false;
      }
      if (!needle) {
        return true;
      }
      return customer.company.toLowerCase().includes(needle) || customer.ownerEmail.toLowerCase().includes(needle) || customer.id.includes(needle);
    });
  }, [query, stateFilter, planFilter]);
  const mrr = rows.reduce((sum, customer) => sum + customer.mrr, 0);
  return <div className="space-y-5" data-id="element-573" data-mp="23wobf77wal7l">
      <header data-id="element-574" data-mp="23xpvu2aswk1z">
        <h1 className="text-xl font-semibold tracking-tight text-ink" data-id="element-575" data-mp="23z0x9vgrxfpx">
          Customers
        </h1>
        <p className="mt-1 text-sm text-ink-muted" data-id="element-576" data-mp="28xvewu308y8g">
          {customerAccounts.length} accounts. Open one to see its monitors,
          invoices and alert delivery.
        </p>
      </header>

      <Panel data-id="element-577" data-mp="2humhlpc2bbc0">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-5 py-4" data-id="element-578" data-mp="iryz05fj1bks">
          <div className="relative min-w-[220px] flex-1" data-id="element-579" data-mp="ipl7u7nywjii">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" aria-hidden="true" data-id="element-580" data-mp="du5wbmdfmzp9" />
            <label htmlFor="customer-search" className="sr-only" data-id="element-581" data-mp="mtatopd9cmzu">
              Search customers
            </label>
            <TextInput id="customer-search" placeholder="Company, email or account id" value={query} onChange={event => setQuery(event.target.value)} className="pl-9" data-id="element-582" data-mp="11h5ihuo5a77n" />
          </div>
          <div className="w-[150px]" data-id="element-583" data-mp="16h0doaeydiyi">
            <label htmlFor="state-filter" className="sr-only" data-id="element-584" data-mp="16i1me88hxtd2">
              Filter by state
            </label>
            <SelectInput id="state-filter" value={stateFilter} onChange={event => setStateFilter(event.target.value as StateFilter)} options={[{
            value: 'all',
            label: 'Any state'
          }, {
            value: 'active',
            label: 'Active'
          }, {
            value: 'past_due',
            label: 'Past due'
          }, {
            value: 'trialing',
            label: 'Trialing'
          }, {
            value: 'cancelled',
            label: 'Cancelled'
          }]} data-id="element-585" data-mp="3ccktntugh8ab" />
          </div>
          <div className="w-[140px]" data-id="element-586" data-mp="2efxmifq3yady">
            <label htmlFor="plan-filter" className="sr-only" data-id="element-587" data-mp="2ej3fi61uxwey">
              Filter by plan
            </label>
            <SelectInput id="plan-filter" value={planFilter} onChange={event => setPlanFilter(event.target.value as PlanFilter)} options={[{
            value: 'all',
            label: 'Any plan'
          }, {
            value: 'starter',
            label: 'Starter'
          }, {
            value: 'standard',
            label: 'Standard'
          }, {
            value: 'pro',
            label: 'Pro'
          }]} data-id="element-588" data-mp="2jdph5hi50alu" />
          </div>
        </div>

        {rows.length === 0 ? <EmptyState icon={SearchIcon} title="No accounts match" body="Try a different search term, or clear the state and plan filters." data-id="element-589" data-mp="1zrsuhxikr0al" /> : <div className="overflow-x-auto" data-id="element-590" data-mp="1qs5p70mq5ac9">
            <table className="w-full min-w-[860px] border-collapse text-left" data-id="element-591" data-mp="1qtgu53rwoo4y">
              <caption className="sr-only" data-id="element-592" data-mp="1ltvuer4wwbr6">
                All customer accounts with plan, usage and revenue
              </caption>
              <thead data-id="element-593" data-mp="1gzromb7du9ro">
                <tr className="border-b border-line bg-canvas" data-id="element-594" data-mp="1c1yg3bilx3o8">
                  {['Account', 'Plan', 'Services', 'State', 'Alerts 30d', 'MRR', 'Last seen'].map(heading => <th key={heading} scope="col" className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-595" data-mp="y38izvvsf3zv">
                      {heading}
                    </th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-line" data-id="element-596" data-mp="3npv0taz31zru">
                {rows.map(customer => {
              const plan = getPlan(customer.planId);
              const atCeiling = customer.servicesUsed >= plan.services;
              return <tr key={customer.id} className="transition-colors duration-150 ease-snap hover:bg-canvas" data-id="element-597" data-mp="2v1fl0m84ndfg">
                      <th scope="row" className="px-5 py-3 font-normal" data-id="element-598" data-mp="34s0pgqahfc6a">
                        <Link to={`/admin/customers/${customer.id}`} className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-599" data-mp="27c4tzwgkwxei">
                          <span className="block text-[13px] font-medium text-ink" data-id="element-600" data-mp="3fj91gwozkwf0">
                            {customer.company}
                          </span>
                          <span className="block font-mono text-[11px] text-ink-soft" data-id="element-601" data-mp="3oddy5wqsx22g">
                            {customer.ownerEmail}
                          </span>
                        </Link>
                      </th>
                      <td className="px-5 py-3 text-[13px] text-ink-muted" data-id="element-602" data-mp="2wou3naeev9sr">
                        {plan.name}
                      </td>
                      <td className="px-5 py-3" data-id="element-603" data-mp="35nxniglejt8b">
                        <span className={`font-mono text-[13px] ${atCeiling ? 'text-warn-ink' : 'text-ink'}`} data-id="element-604" data-mp="35opefe4212s9">
                          {customer.servicesUsed}/{plan.services}
                        </span>
                      </td>
                      <td className="px-5 py-3" data-id="element-605" data-mp="oel5amqcj48n">
                        <AccountStateBadge state={customer.state} data-id="element-606" data-mp="odt7d59a0uf7" />
                      </td>
                      <td className="px-5 py-3 font-mono text-[13px] text-ink-muted" data-id="element-607" data-mp="si9c3375lvsu">
                        {customer.alertsSent30d}
                      </td>
                      <td className="px-5 py-3 font-mono text-[13px] text-ink" data-id="element-608" data-mp="jf86r4q448q8">
                        ${customer.mrr}
                      </td>
                      <td className="px-5 py-3 text-xs text-ink-soft" data-id="element-609" data-mp="176dv68a4c9c5">
                        {relativeTime(customer.lastSeenAt)}
                      </td>
                    </tr>;
            })}
              </tbody>
            </table>
          </div>}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line bg-canvas px-5 py-3" data-id="element-610" data-mp="12db17z1fixvf">
          <p className="text-xs text-ink-muted" data-id="element-611" data-mp="1q2dhb2gf0int">
            Showing {rows.length} of {customerAccounts.length} accounts
          </p>
          <p className="font-mono text-[13px] font-medium text-ink" data-id="element-612" data-mp="3qy1n3ha1dy3p">
            ${mrr}/mo in view
          </p>
        </div>
      </Panel>
    </div>;
}